import { AuthService } from 'src/rest/authService';
import { GlobalUtils } from 'src/utils/globalUtils';
import { MKS_BACKEND_URL } from 'src/utils/env';
import { StatusCode } from 'src/common/constants/constantEnums';
import axios from 'axios';

let isRefreshing = false;
let failedQueue: { resolve: (token?: string | null) => void; reject: (err: any) => void }[] = [];

// Set global axios defaults
axios.defaults.baseURL = MKS_BACKEND_URL;
axios.defaults.withCredentials = true;

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(p => {
    error ? p.reject(error) : p.resolve(token);
  });
  failedQueue = [];
};

axios.interceptors.request.use(
  (config) => {
    // List of endpoints that should NOT have the Authorization header
    const excludedEndpoints = ['/login', '/logout', '/refresh-token', '/register', '/forgot-password', '/reset-password'];
    if (config.url && excludedEndpoints.some(endpoint => config?.url?.includes(endpoint))) {
      return config;
    }

    const token = GlobalUtils.getToken();
    if (token) {
      config.headers = config.headers || {};
      (config.headers as any)['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error?.config;

    if (
      error?.response?.status === StatusCode.NOT_AUTHORIZED_CODE &&
      originalRequest &&
      !originalRequest._retry &&
      !(originalRequest.url && originalRequest.url.includes('/refresh-token'))
    ) {
      // If refresh already in progress → queue request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (token) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
          }
          return axios(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Leverage existing /refresh-token API which uses HttpOnly cookie
        const refreshResponse = await AuthService.refreshToken();
        const newAccessToken: string | undefined = refreshResponse.data?.token;
        if (!newAccessToken) {
          throw new Error('No access token returned from refresh-token endpoint');
        }

        GlobalUtils.setToken(newAccessToken);
        processQueue(null, newAccessToken);

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return axios(originalRequest);

      } catch (refreshError) {
        processQueue(refreshError, null);
        if (typeof (GlobalUtils as any).clearSession === 'function') {
          (GlobalUtils as any).clearSession();
        } else {
          try {
            localStorage.clear();
          } catch {
            // ignore
          }
        }

        globalThis.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);